import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  ArrowRight,
  Loader2,
  Check,
  Menu,
  X,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import MindfullLogo from '@/components/MindfullLogo';
import { 
  ProductPreview, 
  Features, 
  Manifesto, 
  HowItWorks, 
  Testimonial, 
  FinalCTA, 
  Footer 
} from '@/components/landing';

const Landing = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Redirect logged-in users to canvas
  useEffect(() => {
    if (!isLoading && user) {
      navigate('/canvas');
    }
  }, [user, isLoading, navigate]);

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!waitlistEmail.trim()) {
      toast.error('Please enter your email');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(waitlistEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase
        .from('waitlist')
        .insert({ email: waitlistEmail.toLowerCase().trim() });
      
      if (error) {
        if (error.code === '23505') {
          // Unique constraint violation - email already exists
          toast.info("You're already on the list! We'll be in touch soon.");
          setIsSubmitted(true);
        } else {
          throw error;
        }
      } else {
        setIsSubmitted(true);
        toast.success("You're on the list!");
      }
    } catch (error) {
      console.error('Waitlist signup error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f0eb]">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="scrollable-page min-h-screen bg-[#f5f0eb]">
      {/* Main content card with rounded corners */}
      <div className="bg-white mx-4 sm:mx-6 lg:mx-8 my-4 sm:my-6 lg:my-8 rounded-[2rem] sm:rounded-[3rem] shadow-xl overflow-hidden">
        {/* Navigation */}
        <nav className="px-6 sm:px-10 lg:px-16 py-6 sticky top-0 bg-white/95 backdrop-blur-sm z-50 border-b border-transparent">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between">
              <MindfullLogo size="md" />
              
              {/* Desktop nav */}
              <div className="hidden sm:flex items-center gap-4">
                <Link to="/login">
                  <Button variant="ghost" className="font-medium text-muted-foreground hover:text-foreground">
                    Sign In
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button className="rounded-full px-6 bg-[#ff5c35] hover:bg-[#e54d2a] text-white shadow-lg shadow-[#ff5c35]/20">
                    Get Early Access
                  </Button>
                </Link>
              </div>
              
              {/* Mobile hamburger */}
              <button 
                className="sm:hidden p-2 -mr-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-foreground" />
                ) : (
                  <Menu className="w-6 h-6 text-foreground" />
                )}
              </button>
            </div>
            
            {/* Mobile menu dropdown */}
            {mobileMenuOpen && (
              <div className="sm:hidden mt-4 pb-2 flex flex-col gap-2 animate-in slide-in-from-top-2 duration-200">
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-center font-medium text-muted-foreground hover:text-foreground">
                    Sign In
                  </Button>
                </Link>
                <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full rounded-full bg-[#ff5c35] hover:bg-[#e54d2a] text-white shadow-lg shadow-[#ff5c35]/20">
                    Get Early Access
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </nav>

        {/* Hero Section */}
        <section className="pt-16 sm:pt-24 lg:pt-32 pb-8 sm:pb-12 px-6 sm:px-10 lg:px-16">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-5xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-foreground mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700 leading-[1.1]">
                A place for everything
                <br />
                <span className="text-muted-foreground/60">on your mind.</span>
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground mb-12 max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100">
                An infinite canvas for overthinkers, overdoers, and anyone whose mind feels like a handful. Save ideas, inspiration, and references — AI helps you find them later.
              </p>
              
              {/* Waitlist Form */}
              <div className="max-w-md mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
                {isSubmitted ? (
                  <div className="bg-[#ff5c35]/10 border border-[#ff5c35]/20 rounded-2xl p-6 text-center">
                    <div className="w-12 h-12 bg-[#ff5c35] rounded-full flex items-center justify-center mx-auto mb-4">
                      <Check className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">You're on the list!</h3>
                    <p className="text-muted-foreground text-sm">
                      We'll notify you when Mindfull is ready. Keep an eye on your inbox.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleWaitlistSubmit} className="flex flex-col sm:flex-row gap-3">
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      value={waitlistEmail}
                      onChange={(e) => setWaitlistEmail(e.target.value)}
                      disabled={isSubmitting}
                      className="h-14 px-6 text-base rounded-full flex-1 border-border/50 bg-muted/30"
                    />
                    <Button 
                      type="submit"
                      size="lg" 
                      className="gap-2 text-base px-8 h-14 rounded-full bg-[#ff5c35] hover:bg-[#e54d2a] text-white shadow-lg shadow-[#ff5c35]/25 hover:shadow-xl hover:shadow-[#ff5c35]/30 transition-all"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Joining...
                        </>
                      ) : (
                        <>
                          Join Waitlist
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>

          </div>
        </section>

        {/* Product Preview Section */}
        <ProductPreview />

        {/* How It Works Section */}
        <HowItWorks />

        {/* Manifesto Section */}
        <Manifesto />

        {/* Features Grid Section */}
        <Features />

        {/* Testimonial Section */}
        <Testimonial />

        {/* Final CTA Section */}
        <FinalCTA />

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default Landing;
